import { createDecorator } from 'vue-class-component';
import Vue from 'vue';
import delay from 'lodash/delay';

type DebounceTime<V> = number | ((this: V) => number);

type DebounceOptions<V> = DebounceTime<V> | {
  time: DebounceTime<V>;
};

export function Debounce<V extends Vue>(options: DebounceOptions<V>) {
  const time = typeof options === 'object'
    ? options.time
    : options;

  return function(target: V, key: string) {
    createDecorator((componentOptions, methodName) => {
      const debouncedMethodName = `_debounced_${methodName}`;
      const originalMethod = componentOptions.methods![methodName];

      const timeMethodName = `_debounced_${methodName}_time`;
      const timeMethod = typeof time === 'function' ? time : () => time;
      const debouncedTimerName = `_debounced_${methodName}_timerId`;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const newMethod = function(this: any) {
        if (this[debouncedTimerName]) {
          clearTimeout(this[debouncedTimerName]);
        }

        const debounceTime: number = this[timeMethodName]();
        this[debouncedTimerName] = delay(this[debouncedMethodName], debounceTime);
      };
      newMethod.overrideOthers = true;

      componentOptions.mixins = componentOptions.mixins || [];
      componentOptions.mixins.push({
        methods: {
          [debouncedMethodName]: originalMethod,
          [timeMethodName]: timeMethod as (this: Vue) => number,
          [methodName]: newMethod,
        },
        data: () => ({
          [debouncedTimerName]: null,
        }),
      });
    })(target, key);
  };
}
